/**
 * AdminLocationEditor — full-screen editor for one location article.
 *
 * Reached from /admin/location/:slug (linked from AdminLocations table).
 * Loads the doc via GET /api/locations/{slug}, lets the editor tweak any
 * of the 48 encyclopedia sections + the outbound links list, and saves
 * via PATCH /api/locations/{slug} (existing endpoint).
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import {
  Loader2, Save, ArrowLeft, Sparkles, Trash2, Plus, ExternalLink,
} from "lucide-react";

const SECTION_LABELS = {
  basic: "Basic Information",
  overview: "Overview",
  history: "History",
  geography: "Geography",
  topography: "Topography",
  weather: "Weather & Climate",
  population: "Population",
  government: "Government",
  economy: "Economy",
  industries: "Industries",
  agriculture: "Agriculture",
  housing: "Housing",
  neighborhoods: "Neighborhoods",
  transportation: "Transportation",
  infrastructure: "Infrastructure",
  education: "Education",
  healthcare: "Healthcare",
  emergency: "Emergency Services",
  businesses: "Businesses",
  restaurants: "Restaurants & Food",
  tourism: "Tourism",
  recreation: "Recreation",
  parks_nature: "Parks & Nature",
  waterways: "Rivers, Lakes & Waterways",
  arts_culture: "Arts & Culture",
  religion: "Religion",
  sports: "Sports",
  events: "Events",
  media: "Media",
  personalities: "Prominent Personalities",
  companies: "Notable Companies",
  sister_cities: "Sister Cities",
  local_orgs: "Local Organizations",
  education_stats: "Education Statistics",
  utilities: "Public Utilities",
  safety: "Safety",
  environment: "Environment",
  future: "Future Development",
  demographics: "Demographics",
  facts: "Interesting Facts",
  faq: "FAQ",
  images: "Images",
  videos: "Videos",
  maps: "Maps",
  community: "Social Pulse Community",
  seo: "Metadata for Search",
  references: "References",
};

const SECTION_KEYS = Object.keys(SECTION_LABELS);

export default function AdminLocationEditor() {
  const params = useParams();
  const nav = useNavigate();
  const slug = params["*"] || params.slug;

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    http.get(`/locations/${slug}`)
      .then((r) => setDoc(r.data))
      .catch(() => toast.error("Failed to load location"))
      .finally(() => setLoading(false));
  }, [slug]);

  const filledCount = useMemo(
    () => Object.values(doc?.sections || {}).filter((v) => String(v || "").trim()).length,
    [doc],
  );

  const updateSection = (key, value) => {
    setDoc((d) => ({ ...d, sections: { ...(d?.sections || {}), [key]: value } }));
  };

  const updateLink = (i, patch) => {
    setDoc((d) => {
      const links = [...(d?.links || [])];
      links[i] = { ...links[i], ...patch };
      return { ...d, links };
    });
  };
  const addLink = () => {
    setDoc((d) => ({ ...d, links: [...(d?.links || []), { caption: "", url: "", kind: "" }] }));
  };
  const removeLink = (i) => {
    setDoc((d) => ({ ...d, links: (d?.links || []).filter((_, ii) => ii !== i) }));
  };

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const { data } = await http.patch(`/locations/${doc.slug}`, {
        sections: doc.sections || {},
        links: (doc.links || []).filter((l) => l.caption && l.url),
        founded_date: doc.founded_date ?? null,
      });
      setDoc(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      await http.post(`/admin/locations/${doc.slug}/regenerate`, { mode: "overwrite" });
      toast.success("Regenerating with AI — poll every ~30s or refresh the page.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regen failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the article at ${doc.slug}? This cannot be undone.`)) return;
    try {
      await http.delete(`/locations/${doc.slug}`);
      toast.success("Deleted.");
      nav("/admin?tab=locations");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-50" /></div>;
  if (!doc) return <div className="py-20 text-center opacity-60 text-sm">Article not found.</div>;

  return (
    <article className="max-w-6xl mx-auto px-5 py-8" data-testid="admin-location-editor">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <Link to="/admin?tab=locations" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100">
            <ArrowLeft className="w-4 h-4" /> Locations
          </Link>
          <h1 className="font-display text-3xl mt-2" data-testid="editor-title">{doc.name}</h1>
          <div className="text-xs opacity-70 mt-1 font-mono">
            {doc.slug} · {doc.kind} · {filledCount}/48 sections filled
          </div>
          {/* Founding date — powers the "Weather on the founding" panel
              on village / heritage-site pages. Optional. */}
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="founded-date-row">
            <label className="text-xs font-mono uppercase tracking-widest opacity-70">
              Founding date
            </label>
            <input
              type="date"
              value={(doc.founded_date || "").slice(0, 10)}
              onChange={(e) => setDoc((d) => ({ ...d, founded_date: e.target.value || null }))}
              max={new Date().toISOString().slice(0, 10)}
              data-testid="founded-date-input"
              className="rounded-full border px-3 py-1 text-xs bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
            />
            {doc.founded_date && (
              <button
                type="button"
                onClick={() => setDoc((d) => ({ ...d, founded_date: null }))}
                data-testid="founded-date-clear"
                className="text-xs underline underline-offset-4 opacity-70"
              >
                clear
              </button>
            )}
            <span className="text-[10px] opacity-50">
              Shows historical weather for this date on the live page.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={`/l/${doc.slug}`} target="_blank" rel="noreferrer" className="wofy-btn-secondary inline-flex text-sm">
            Open live <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button onClick={regenerate} disabled={busy} className="wofy-btn-secondary inline-flex text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Regenerate
          </button>
          <button onClick={remove} className="wofy-btn-ghost inline-flex text-sm text-red-600" data-testid="delete-loc">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button onClick={save} disabled={saving} className="wofy-btn-primary inline-flex text-sm" data-testid="save-loc">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Section rail */}
        <aside className="text-sm max-h-[70vh] overflow-y-auto sticky top-4 self-start">
          {SECTION_KEYS.map((k) => {
            const filled = !!String(doc.sections?.[k] || "").trim();
            const active = k === activeSection;
            return (
              <button
                key={k}
                onClick={() => setActiveSection(k)}
                className="w-full text-left px-2 py-1.5 rounded transition flex items-center gap-2"
                style={active
                  ? { background: "#EEF2FF", color: "#4338CA", fontWeight: 600 }
                  : {}}
                data-testid={`sec-rail-${k}`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: filled ? "#16A34A" : "#E5E7EB" }}
                />
                <span className={filled ? "" : "opacity-60"}>{SECTION_LABELS[k]}</span>
              </button>
            );
          })}
        </aside>

        {/* Editor for the active section */}
        <div>
          <div className="rounded-2xl border p-5" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card,#fff)" }}>
            <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">{activeSection}</div>
            <h2 className="font-display text-xl mb-3">{SECTION_LABELS[activeSection]}</h2>
            <textarea
              data-testid={`sec-textarea-${activeSection}`}
              value={doc.sections?.[activeSection] || ""}
              onChange={(e) => updateSection(activeSection, e.target.value)}
              placeholder="Write Markdown content for this section…"
              rows={22}
              className="w-full font-mono text-sm px-3 py-2 rounded-lg border outline-none"
              style={{ borderColor: "var(--wofy-border)", background: "#FAFAFA" }}
            />
            <div className="text-xs opacity-60 mt-2">Markdown supported · headings with `##`, bullets with `- `, tables, links.</div>
          </div>

          {/* Links editor */}
          <div className="rounded-2xl border p-5 mt-5" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card,#fff)" }} data-testid="links-editor">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Useful links ({(doc.links || []).length})</h3>
              <button onClick={addLink} className="wofy-btn-secondary inline-flex text-sm"><Plus className="w-3.5 h-3.5" /> Add link</button>
            </div>
            <div className="space-y-2">
              {(doc.links || []).map((l, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_140px_36px] gap-2" data-testid={`link-row-${i}`}>
                  <input value={l.caption || ""} onChange={(e) => updateLink(i, { caption: e.target.value })} placeholder="Caption" className="px-2 py-1.5 rounded-md border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
                  <input value={l.url || ""} onChange={(e) => updateLink(i, { url: e.target.value })} placeholder="https://…" className="px-2 py-1.5 rounded-md border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
                  <input value={l.kind || ""} onChange={(e) => updateLink(i, { kind: e.target.value })} placeholder="kind (gov, wikipedia…)" className="px-2 py-1.5 rounded-md border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
                  <button onClick={() => removeLink(i)} className="p-1 opacity-60 hover:opacity-100 text-red-600" data-testid={`link-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {(doc.links || []).length === 0 && (
                <div className="py-6 text-center text-sm opacity-60">No links yet — add government portals, Wikipedia, tourism boards…</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
